import { mdiFeather } from '@mdi/js'
import Icon from '@mdi/react'
import ImageIcon from '@mui/icons-material/Collections'
import LinkIcon from '@mui/icons-material/Link'
import BulkIcon from '@mui/icons-material/PlaylistAdd'
import VideoIcon from '@mui/icons-material/Videocam'
import Button from '@mui/material/Button'
import { ARTICLE_TYPE, GIF_TYPE, VIDEO_TYPE } from 'interfaces'
import { Fragment, useCallback, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Routes, useLocation, useMatch, useNavigate } from 'react-router'
import { setComposerBucket } from 'services/compose'
import {
  composerContentItemSelector,
  composerImageUrlsSelector,
  composerLinkSelector,
  composerMultiPostSelector,
  composerSelectedProfilesSelector,
  composerTabsSelector,
  composerVideoUrlSelector,
  multiPostsCountSelector
} from 'services/compose/selectors'
import { BulkContentPost } from 'services/compose/state'
import { selectedContentItemsSelector } from 'services/content/selectors'
import { profilesByPpidSelector } from 'services/destinations'
import { setSelectedProfiles } from 'services/post'
import { bucketsSelector } from 'services/post/selectors'
import { activeProductsSelector, RECOMMENDED_PRODUCT_ID } from 'services/product'
import { userProductSelector } from 'services/users/selectors'
import { groupByKey, numberToHuman } from 'shared/utils'
import { RouteErrorView } from '../ErrorFallback/RouteErrorView'
import styles from './AppNavigation.pcss'
import ContentNav from './ContentNav'
import { MainNav } from './MainNav'
import PublishingNav from './publishing/PublishingNav'
import { SettingsNav } from './SettingsNav'
import { TrainingNav } from './TrainingNav'
import { BRAND_TWITTER } from 'shared'

export function AppNavigationSidebar() {
  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const buckets = useSelector(bucketsSelector)
  const profiles = useSelector(profilesByPpidSelector)
  const bucketsViewMatch = useMatch('/posts/buckets/:id/*')
  const userProduct = useSelector(userProductSelector)
  const products = useSelector(activeProductsSelector)

  const isHomepage = location.pathname.startsWith('/home')

  const showUpgradeLink = useMemo(() => {
    const recommendedProduct = products.find(p => p.id === RECOMMENDED_PRODUCT_ID)
    if (!userProduct || !recommendedProduct) {
      return false
    }

    const order = userProduct.order
    return order < recommendedProduct.order
  }, [products, userProduct])

  const openComposer = useCallback(() => {
    if (bucketsViewMatch?.params?.id) {
      const bucket = buckets.find(b => b.id === bucketsViewMatch.params.id)
      if (bucket && bucket.ppids.length > 0) {
        const bucketProfiles = bucket.ppids.map(ppid => profiles[ppid]).filter(Boolean)
        dispatch(setComposerBucket(bucketsViewMatch.params.id))
        dispatch(setSelectedProfiles({ profiles: bucketProfiles }))
      }
    }
    navigate('/composer', { state: { floating: true, modal: 'compose', opener: location.pathname, background: location } })
  }, [bucketsViewMatch?.params.id, navigate, location, buckets, dispatch, profiles])

  const onUpgradeClick = () => {
    navigate('/settings/subscription')
  }

  return (
    <div className={`${styles.nav} ${isHomepage ? '' : styles.expanded}`}>
      <MainNav className={styles['main-nav']} />
      {!isHomepage && (
        <div className={styles.subnav}>
          <div className={styles['btn-box']}>
            <ComposerButton onClick={openComposer} />
          </div>
          <div className={`${styles.scrollable} ${showUpgradeLink ? styles.withFooter : ''}`}>
            <Routes>
              <Route path="*" errorElement={<RouteErrorView />}>
                <Route path="content/search/*" element={<ContentNav expanded />} />
                <Route path="content/*" element={<ContentNav expanded />} />
                <Route path="settings/*" element={<SettingsNav />} />
                <Route path="calendar/*" element={<PublishingNav activeView="calendar" />} />
                <Route path="plans/*" element={<PublishingNav activeView="plans" />} />
                <Route path="posts/buckets/*" element={<PublishingNav activeView="posts" hideProfiles />} />
                <Route path="posts/*" element={<PublishingNav activeView="posts" />} />
                <Route path="history/*" element={<PublishingNav activeView="history" />} />
                <Route path="training/*" element={<TrainingNav />} />
                <Route path="*" element={null} />
              </Route>
            </Routes>
          </div>
          {showUpgradeLink && (
            <div className={styles.footer}>
              <p><FormattedMessage id="app.nav.upgrade-footer.message" /></p>
              <Button
                color="primary"
                variant="contained"
                size="small"
                fullWidth
                onClick={onUpgradeClick}
              >
                <FormattedMessage id="app.nav.upgrade-footer.action" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ComposerButton({ onClick }: { onClick: () => void }) {
  const selectedContentItems = useSelector(selectedContentItemsSelector)
  const multiPost = useSelector(composerMultiPostSelector)
  const multiPostsCount = useSelector(multiPostsCountSelector)
  const selectedNetworks = useSelector(composerTabsSelector)
  const profiles = useSelector(composerSelectedProfilesSelector)
  const profilesByNetwork = groupByKey(profiles, 'type')

  const postsByType = Object.values(multiPost.posts).reduce((map: Record<string, number>, post: BulkContentPost) => {
    if (!selectedNetworks.includes(post.network)) {
      return map
    }

    const profilesCount = profilesByNetwork[post.network]?.length || 0
    if (post.imageUrl) {
      map.photos += profilesCount
    } else {
      map.articles += profilesCount
    }
    return map
  }, { photos: 0, articles: 0 })
  const composerImages = useSelector(composerImageUrlsSelector)
  const composerContentItem = useSelector(composerContentItemSelector)
  const composerLink = useSelector(composerLinkSelector)
  const composerVideoUrl = useSelector(composerVideoUrlSelector)
  const isLinkPost = composerContentItem?.type === ARTICLE_TYPE || composerLink?.type === 'link'
  const isTwitterGif = composerContentItem?.type === GIF_TYPE && composerContentItem?.feed.type === BRAND_TWITTER
  const isVideoPost = Boolean(composerVideoUrl)
    || composerLink?.type === 'video_link'
    || isTwitterGif
    || composerContentItem?.type === VIDEO_TYPE

  if (isLinkPost) {
    postsByType.articles += 1
  }

  postsByType.photos = composerImages.length || postsByType.photos
  const withContentSelection = Object.keys(selectedContentItems).length > 0

  return (
    <Button
      color="primary"
      variant="contained"
      size="small"
      startIcon={<Icon path={mdiFeather} size="20px" />}
      disabled={withContentSelection}
      className={styles['btn-compose']}
      data-testid="btn-create-post"
      onClick={onClick}
    >
      <FormattedMessage id="header.btn-compose" />
      {!withContentSelection && (
        <Fragment>
          {multiPostsCount > 0 ? (
            <span className={styles['btn-img']}>
              <BulkIcon className={styles['btn-bulk']} />
              {numberToHuman(multiPostsCount)}
            </span>
          ) : (
            <Fragment>
              {postsByType.photos > 0 && (
                <span className={styles['btn-img']}>
                  <ImageIcon className={styles['btn-icon']} />
                  {numberToHuman(postsByType.photos)}
                </span>
              )}
              {(isLinkPost || postsByType.articles > 0) && (
                <span className={styles['btn-img']}>
                  <LinkIcon className={styles['btn-icon']} />
                  {numberToHuman(postsByType.articles)}
                </span>
              )}
              {isVideoPost && (
                <span className={styles['btn-img']}>
                  <VideoIcon className={styles['btn-icon']} />
                </span>
              )}
            </Fragment>
          )}
        </Fragment>
      )}
    </Button>
  )
}

export default AppNavigationSidebar
